import { API_BASE } from "./api";

/**
 * Resolves a backend relative image path to a full URL, or returns the fallback image if path is empty.
 * @param path - The image path from backend (can be relative, absolute or full url).
 * @param fallback - The fallback image path to use if the input path is null or empty.
 */
export function getImage(path?: string | null, fallback = "/default-avatar.png"): string {
  if (!path) return fallback;
  if (path.startsWith("http://") || path.startsWith("https://")) return path;
  const cleanPath = path.startsWith("/") ? path : `/${path}`;
  return `${API_BASE}${cleanPath}`;
}

export function formattimeAgo(dateString?: string | null): string {
  if (!dateString) return "";
  const now = new Date();
  const past = new Date(dateString);
  if (isNaN(past.getTime())) return String(dateString);
  const seconds = Math.floor((now.getTime() - past.getTime()) / 1000);

  const intervals = [
    { label: "year", seconds: 365 * 24 * 60 * 60 },
    { label: "month", seconds: 30 * 24 * 60 * 60 },
    { label: "week", seconds: 7 * 24 * 60 * 60 },
    { label: "day", seconds: 24 * 60 * 60 },
    { label: "hour", seconds: 60 * 60 },
    { label: "minute", seconds: 60 },
    { label: "second", seconds: 1 },
  ];

  for (const interval of intervals) {
    const count = Math.floor(seconds / interval.seconds);
    if (count >= 1) {
      if (count === 1) {
        return `a ${interval.label} ago`;
      } else {
        return `${count} ${interval.label}s ago`;
      }
    }
  }

  return "just now";
}

export function formatFollowerCount(input: string | number): string {
  const num = typeof input === "string" ? parseFloat(input) : input;
  if (isNaN(num)) return "0";

  if (num < 1000) {
    return num.toLocaleString("en-US");
  } else if (num < 1_000_000) {
    return (num / 1000).toFixed(1).replace(/\.0$/, "") + "K";
  } else if (num < 1_000_000_000) {
    return (num / 1_000_000).toFixed(1).replace(/\.0$/, "") + "M";
  } else {
    return (num / 1_000_000_000).toFixed(1).replace(/\.0$/, "") + "B";
  }
}

export function formatNumberWithCommas(input: string | number): string {
  const num = typeof input === "string" ? parseFloat(input) : input;
  if (isNaN(num)) return "0";
  return num.toLocaleString("en-US");
}

export function formatTime(timeInSeconds: number): string {
  const minutes = Math.floor(timeInSeconds / 60);
  const seconds = Math.floor(timeInSeconds % 60);
  return `${minutes}:${seconds.toString().padStart(2, "0")}`;
}
